import PropTypes from 'prop-types'
import React from 'react'
import cc from 'classcat'

import { AddToCartNotice } from '../../templateComponents/Buybox'
import { AmazonButton, CheckoutButton, PaypalButton } from '../../templateComponents/Cart/CheckoutButtons'
import CartEmptyNote from '../epages.base/CartEmptyNote'
import CartTemplateComponent from '../../templateComponents/Cart/Cart'
import Coupons from '../../templateComponents/Cart/Coupons'
import EnergyLabel from '../../templateComponents/EnergyLabel'
import LazyImage from '../../LazyImage'
import LineItem from '../../templateComponents/Cart/LineItem'
import LineItems from '../../templateComponents/Cart/LineItems'
import Link from '../../templateComponents/Link'
import PlaceholderImage from '../../PlaceholderImage'

export default function Cart({ cart, trackProductClick, shop, displayWishlistError, t, invalidProducts }) {
  return (
    <CartTemplateComponent>
      <div>
        {!cart.totalNumberOfItems && <CartEmptyNote shopHref={shop.href} t={t} />}

        {cart.totalNumberOfItems > 0 && (
          <div className="cart">
            <section className="section">
              <div className="wrapper">
                <div className="content">
                  <h1>{t('views.storefrontView.basketPageSection.title')}</h1>

                  {cart.quantityMismatch && (
                    <div className="cart-warning-message">
                      {t('views.storefrontView.basketPageSection.productQuantityTooHighExplanation')}
                    </div>
                  )}
                  {cart.couponSuccess && cart.checkoutState && !cart.coupon.hasValidationErrors && (
                    <div className="cart-success-message">
                      {t('views.storefrontView.basketPageSection.couponInputField.successMessage')}
                    </div>
                  )}
                  {cart.checkoutState && !cart.checkoutState.priceValidToOrder && (
                    <div className="cart-warning-message">
                      {t('views.storefrontView.basketPageSection.checkoutValidation.cartMinimumAmountNotReached', {
                        amount: cart.minimumOrderValue.formatted,
                      })}
                    </div>
                  )}
                  {invalidProducts.length > 0 && (
                    <div className="cart-warning-message">
                      <span>
                        {t('views.storefrontView.basketPageSection.invalidProducts')}
                        <ul>
                          {invalidProducts.map((item) => (
                            <li key={`ip-${item.name}`}>{item.name}</li>
                          ))}
                        </ul>
                      </span>
                    </div>
                  )}
                  {displayWishlistError && (
                    <div className="cart-warning-message">
                      {t('views.storefrontView.basketPageSection.wishlistItemsNotAddedCompletely')}
                    </div>
                  )}
                  <Coupons.ValidationInfo errorClass="cart-error-message" warningClass="cart-warning-message" />

                  <table className="cart-table">
                    <thead className="cart-table-head">
                      <tr>
                        <th className="cart-table-head-product">
                          <span>{t('components.productComponent.product')}</span>
                        </th>
                        <th className="cart-table-head-price">
                          <span>{t('views.storefrontView.basketPageSection.singlePrice')}</span>
                        </th>
                        <th className="cart-table-head-quantity">
                          <span>{t('views.storefrontView.basketPageSection.quantity')}</span>
                        </th>
                        <th className="cart-table-head-total">
                          <span>{t('views.storefrontView.basketPageSection.totalPrice')}</span>
                        </th>
                      </tr>
                    </thead>
                    <LineItems
                      tagName="tbody"
                      renderItem={(lineItem, lineItemIndex) => (
                        <tr>
                          <td className="cart-table-item-product">
                            <Link to={lineItem.productUrl} onClick={() => trackProductClick(lineItem, lineItemIndex)}>
                              {lineItem.imageUrl ? (
                                <LazyImage src={lineItem.imageUrl} className="cart-table-item-product-image" />
                              ) : (
                                <PlaceholderImage className="cart-table-item-product-image" />
                              )}
                            </Link>
                            <div className="cart-table-item-product-description">
                              <h2 className="headline">
                                <Link
                                  to={lineItem.productUrl}
                                  onClick={() => trackProductClick(lineItem, lineItemIndex)}
                                >
                                  {lineItem.name}
                                </Link>
                              </h2>
                              {lineItem.variationString && (
                                <p className="variation-values">{lineItem.variationString}</p>
                              )}
                              {lineItem.essentialFeatures.length > 0 && (
                                <p className="essential-features">
                                  {lineItem.essentialFeatures.map((essentialFeature, essentialFeatureIndex) => (
                                    <span key={essentialFeatureIndex}>
                                      {essentialFeature}
                                      <br />
                                    </span>
                                  ))}
                                </p>
                              )}
                              {lineItem.energyLabel && (
                                <EnergyLabel
                                  t={t}
                                  energyLabel={lineItem.energyLabel}
                                  energyLabelSourceFile={lineItem.energyLabelSourceFile}
                                />
                              )}
                              <div className="cart-table-item-product-actions">
                                {!shop.beyond && lineItem.productId && (
                                  <LineItem.AddToWishlistLinkInCart className="add-wishlist-link add-wishlist-icon">
                                    <span className="add-wishlist-text">
                                      {t('views.storefrontView.basketPageSection.addToWishlistLinkInCart.label')}
                                    </span>
                                  </LineItem.AddToWishlistLinkInCart>
                                )}
                                <LineItem.RemoveLineItemButton
                                  className="remove-link remove-icon"
                                  title={t('views.storefrontView.basketPageSection.removeProductButton.label')}
                                >
                                  <span className="remove-text">
                                    {t('views.storefrontView.basketPageSection.removeProductButton.label')}
                                  </span>
                                </LineItem.RemoveLineItemButton>
                              </div>
                            </div>
                          </td>
                          <td className="cart-table-item-price">
                            <span className="show-subhead">
                              {t('views.storefrontView.basketPageSection.singlePrice')}
                            </span>
                            <span className="price-value">{lineItem.singleItemPrice.formatted}</span>
                          </td>
                          <td className="cart-table-item-quantity">
                            <span className="show-subhead">{t('views.storefrontView.basketPageSection.quantity')}</span>
                            <LineItem.QuantityField
                              min={0}
                              invalidProduct={lineItem.productId === null}
                              className="quantity-field"
                            />
                          </td>
                          <td className="cart-table-item-total">
                            <span className="show-subhead">
                              {t('views.storefrontView.basketPageSection.totalPrice')}
                            </span>
                            <span
                              className={cc([
                                'price-value',
                                { 'cart-coupon-discount-indicator-text': lineItem.lineItemCouponDiscount },
                              ])}
                            >
                              {lineItem.lineItemCouponDiscount && (
                                <span className="cart-coupon-discount-indicator-icon"></span>
                              )}
                              {lineItem.lineItemPrice.formatted}
                            </span>
                          </td>
                        </tr>
                      )}
                    ></LineItems>
                  </table>

                  {cart.canHaveCoupon && (
                    <div className="cart-coupon">
                      <Coupons.RedemptionForm className="cart-coupon-redemption-form">
                        <label htmlFor="coupon-form-field" className="cart-coupon-redemption-form-label">
                          {t('views.storefrontView.basketPageSection.couponInputField.label')}
                        </label>
                        <div>
                          <Coupons.RedemptionForm.CouponField
                            id="coupon-form-field"
                            className="cart-coupon-redemption-form-field"
                            errorClass="cart-coupon-redemption-form-field-error"
                          />
                          <Coupons.RedemptionForm.RedeemButton className="cart-coupon-redemption-form-button">
                            <span className="hide-text">
                              {t('views.storefrontView.basketPageSection.couponInputField.redeemButton.label')}
                            </span>
                          </Coupons.RedemptionForm.RedeemButton>
                        </div>
                        <Coupons.CouponError
                          className="invalid-error-message"
                          hiddenClass="invalid-error-message-hidden"
                        />
                      </Coupons.RedemptionForm>
                    </div>
                  )}
                  <div className="cart-totals">
                    <dl className="cart-totals-table">
                      <dt>{t('views.storefrontView.basketPageSection.subtotal')}</dt>
                      <dd>{cart.subAmount}</dd>
                      {cart.coupon && (
                        <>
                          <dt>{t('views.storefrontView.basketPageSection.coupon')}</dt>
                          <dd className="cart-coupon-campaign">
                            <div className="cart-coupon-campaign-description">
                              <span className="cart-coupon-campaign-description-headline">
                                {cart.couponCampaign.name}{' '}
                                <span className="cart-coupon-campaign-description-type">
                                  ({cart.couponCampaign.type.formatted})
                                </span>
                              </span>
                              {typeof cart.coupon.lineItemPrice.amount === 'number' && (
                                <span className="cart-coupon-campaign-description-price">
                                  {cart.coupon.lineItemPrice.formatted}
                                </span>
                              )}
                            </div>
                            <Coupons.RemoveCouponButton className="remove-link remove-icon">
                              <span className="remove-text">
                                {t('views.storefrontView.basketPageSection.couponInputField.removeButton.label')}
                              </span>
                            </Coupons.RemoveCouponButton>
                          </dd>
                        </>
                      )}
                      {cart.shippingLineItem && (
                        <>
                          <dt>{cart.shippingLineItem.shippingMethod.name}</dt>
                          <dd>{cart.shippingLineItem.lineItemPrice.formatted}</dd>
                        </>
                      )}
                      <dt>{t('views.storefrontView.basketPageSection.total')}</dt>
                      <dd className="grand-amount">
                        {cart.grandAmount}
                        <span className="grand-amount-note">{t(cart.grandAmountNote, { nsSeparator: '__' })}</span>
                      </dd>
                    </dl>
                    <div id="ep-app-area-cart-view-cart-totals"></div>
                    <CheckoutButton className="cart-totals-button-checkout">
                      {t('views.storefrontView.basketPageSection.checkoutLinkButton.label')}
                      <span className="checkout-button-icon"></span>
                    </CheckoutButton>
                    <div className="checkout-buttons row">
                      <PaypalButton className="paypal-button" shape="rect" color="gold" />
                      <AmazonButton className="amazon-button" />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
      <AddToCartNotice />
    </CartTemplateComponent>
  )
}

Cart.propTypes = {
  t: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  displayWishlistError: PropTypes.bool.isRequired,
  trackProductClick: PropTypes.func.isRequired,
  invalidProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
}
